/**
 * Paleta de colores de las series del dashboard de estadísticas.
 *
 * Deriva de `src/components/ui/badgeColors.js`, la fuente única de verdad de
 * la paleta corporativa: aquí NO se escribe ni un hexadecimal a mano. Si mañana
 * cambia el verde de los badges, cambia el verde de las gráficas con él, y las
 * cuatro secciones siguen hablando el mismo idioma visual que los chips de
 * estado del resto de la aplicación.
 *
 * Se toma el campo `border` de cada color (el tono saturado original) y no
 * `bg` ni `text`: `bg` es un pastel pensado para ir detrás de un texto y sobre
 * blanco desaparece; `text` está oscurecido para cumplir contraste de lectura y
 * todas las series saldrían casi negras. El `border` es el tono de identidad.
 *
 * Funciones puras, sin estado. No conocen React ni recharts.
 */

import { BADGE_COLORS } from "../../../components/ui/badgeColors";

/**
 * Rojo corporativo. RESERVADO para el estado de carga y los acentos de UI
 * (pestaña activa, botón de aplicar, spinner de las gráficas).
 *
 * No forma parte de COLORES_SERIE y no debe pintar ninguna serie: si una barra
 * es del mismo rojo que el spinner y que la pestaña seleccionada, deja de
 * leerse como dato y pasa a leerse como "esto está seleccionado" o "esto está
 * mal". Vive aquí, y no suelto en cada componente, justo para que la reserva
 * sea visible desde el mismo fichero que define lo que sí puede ser una serie.
 */
export const COLOR_ACENTO = "#cc071e";

/**
 * Variante oscurecida del acento, para el `:hover` de los controles que van
 * rellenos de rojo corporativo (el botón "Aplicar" de los filtros y el chip del
 * periodo activo).
 *
 * Está aquí y no en cada componente porque era el único tono corporativo que se
 * escribía a mano en dos sitios a la vez: si alguien retoca el rojo de la marca
 * en uno y no en el otro, el botón y el chip dejan de ser el mismo rojo al pasar
 * el ratón y nadie lo nota hasta verlos juntos.
 */
export const COLOR_ACENTO_HOVER = "#a30618";

/**
 * Colores de serie, en el orden en que se asignan.
 *
 * ⚠️ EL ORDEN ES INTENCIONADO, NO ALFABÉTICO NI ESTÉTICO. Los siete primeros
 * son tonos sin semántica de estado (azul, verde, ámbar, púrpura, teal, índigo,
 * rosa): identifican categorías —una ruta, un empleado, una métrica— sin
 * afirmar nada sobre ellas.
 *
 * RED va EL ÚLTIMO a propósito: es el único con carga semántica negativa
 * ("crítico / requiere atención" en el sistema de badges). Al estar al final
 * sólo lo alcanza la octava serie de una gráfica, y en la práctica ninguna
 * llega. Quien necesite pintar de rojo una serie que SÍ es negativa
 * (incidencias, pendientes, errores) toma `BADGE_COLORS.RED.border`
 * explícitamente, que se lee como una decisión; nunca lo recibe por sorteo.
 *
 * Si reordenas este array, lee antes el párrafo de arriba: mover RED al
 * principio pinta de rojo la primera serie de las cuatro secciones a la vez.
 *
 * @type {string[]}
 */
export const COLORES_SERIE = [
    BADGE_COLORS.BLUE.border, // #3498db
    BADGE_COLORS.GREEN.border, // #27ae60
    BADGE_COLORS.AMBER.border, // #f39c12
    BADGE_COLORS.PURPLE.border, // #9c27b0
    BADGE_COLORS.TEAL.border, // #009688
    BADGE_COLORS.INDIGO.border, // #3f51b5
    BADGE_COLORS.PINK.border, // #e91e63
    BADGE_COLORS.RED.border, // #e74c3c - semántica negativa, ver comentario
];

/**
 * Subconjunto sin semántica de estado: COLORES_SERIE menos el RED final.
 *
 * Lo usa el reparto por clave (getColorSeriePorClave), donde el color no lo
 * elige nadie: una ruta cualquiera no puede acabar pintada de rojo "de
 * crítico" sólo porque su hash cayó en la última posición.
 */
const COLORES_SERIE_NEUTRAS = COLORES_SERIE.slice(0, -1);

/**
 * Normaliza un índice a una posición válida del array, ciclando.
 * Soporta negativos: -1 devuelve la última posición, no un hueco.
 */
const ciclar = (indice, longitud) => (((indice % longitud) + longitud) % longitud);

/**
 * Color de la serie número `indice`, ciclando al llegar al final del array.
 *
 * Es el reparto POR POSICIÓN: sirve cuando el orden de las series es fijo
 * (las tres métricas de "Por horas", los dos valores de "Por empleado"). Si el
 * orden puede cambiar entre recargas —un ranking que se reordena al cambiar de
 * filtros—, usa getColorSeriePorClave: con éste, la misma ruta cambiaría de
 * color al subir o bajar en la lista y la gráfica sería ilegible de una carga
 * a otra.
 *
 * @param {number} indice - Posición de la serie (0, 1, 2, ...)
 * @returns {string} Color hexadecimal de COLORES_SERIE
 */
export const getColorSerie = (indice) => {
    // Un índice que no es un entero finito (undefined, NaN, "2", null) no se
    // convierte ni se lanza: cae al primer color. Un fallo de color no puede
    // tumbar una gráfica entera, y el primer color es el que ya vería una
    // gráfica de una sola serie.
    if (!Number.isInteger(indice)) return COLORES_SERIE[0];

    return COLORES_SERIE[ciclar(indice, COLORES_SERIE.length)];
};

/**
 * Hash FNV-1a de 32 bits. Determinista, sin dependencias y estable entre
 * ejecuciones, navegadores y recargas: es lo único que hace falta para que la
 * misma cadena devuelva siempre el mismo color.
 */
const hashCadena = (texto) => {
    let hash = 2166136261; // offset basis de FNV-1a (32 bits)

    for (let i = 0; i < texto.length; i += 1) {
        hash ^= texto.charCodeAt(i);
        // imul mantiene la multiplicación en 32 bits; sin él, el producto se
        // va al rango de coma flotante y el hash pierde su distribución.
        hash = Math.imul(hash, 16777619);
    }

    return hash >>> 0; // a entero sin signo
};

/**
 * Color ESTABLE POR CLAVE: la misma clave devuelve siempre el mismo color,
 * independientemente de su posición y de qué otras claves haya en la gráfica.
 *
 * Lo necesita "Pedidos por rutas" (TASK-016): las rutas se ordenan de mayor a
 * menor, así que al cambiar el rango de fechas "TCI NOR 1" salta de la primera
 * barra a la cuarta. Con un color por índice, cambiaría de color en cada
 * recarga y el usuario perdería la referencia visual que acaba de construirse.
 *
 * CÓMO DERIVA EL COLOR: la clave se normaliza (trim + mayúsculas, para que
 * "tci nor 1" y "TCI NOR 1 " sean la misma ruta), se pasa por un hash FNV-1a de
 * 32 bits y el resto de dividir ese hash entre el número de colores neutros da
 * la posición. Determinista y sin estado: no hace falta memorizar asignaciones
 * previas ni pasar la lista completa de claves.
 *
 * ⚠️ NO GARANTIZA UNICIDAD. Con 7 colores neutros, dos rutas distintas pueden
 * compartir color; es el precio de no llevar registro. Es aceptable en barras
 * con etiqueta visible (cada barra dice su nombre), NO en una gráfica donde el
 * color sea el único identificador: ahí usa getColorSerie sobre una lista de
 * orden fijo.
 *
 * @param {string} clave - Identificador de la serie (nombre de ruta, empleado...)
 * @returns {string} Color hexadecimal, siempre de los neutros (nunca el RED)
 */
export const getColorSeriePorClave = (clave) => {
    if (typeof clave !== "string" || clave.trim() === "") return COLORES_SERIE_NEUTRAS[0];

    const normalizada = clave.trim().toUpperCase();

    return COLORES_SERIE_NEUTRAS[hashCadena(normalizada) % COLORES_SERIE_NEUTRAS.length];
};
